/**
 * 信号识别（`#signal`）：一轮里的第 ① 步——读懂客户这句话（拆诉求、打意图、抽业务对象与信息项、判情绪与风险、判接续），
 * 不选工具、不改事项（后台对齐批 4；摸底 C_应答配置 第 6 条）。标签的定义统一在「标签字典」（`#tags`），这页按字典口径取数、不另存一份：
 * - 意图表：概览（带一级），点行跳标签字典那一条看四段说明与被引用；
 * - 业务对象与信息项：读字典「业务对象」「信息项」两个维度（标签体系 v2 批 2：原因、件数、金额、商品属性、问题、地址从实体并进信息项，
 *   标「原为实体」），外加核实后怎么进事项；情绪与风险取值读字典「情绪与风险」维度；表头可跳字典对应维度；
 * - 规则层与确认门：`signal/rules.py`、`signal/gate.py` 常量 ＋ 规则注册表 stage=信号；
 * - 本轮信号留痕：事件 `plan.signal`（`GET /signal/recent`，近 3 天，盲测会话后端已排除），意图显示「一级 › 二级」、点了跳字典，
 *   带情绪与风险两列（可筛）。规则编号都能跳「运行规则」页对应那条。改表、改规则走 PR。
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { JsonBlock } from "@/components/ui/code-block";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import { Tabs } from "@/components/ui/tabs";
import {
	type PlanSignalConfig,
	type PlanSignalIntent,
	type PlanSignalTurn,
	planErrorMessage,
	usePlanSignal,
	usePlanSignalRecent,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { PageJump } from "../kit/page-jump";
import { RESUME_ACTION_ZH, SOURCE_TONE, SOURCE_ZH, zh } from "../kit/plan-labels";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";

type TabKey = "intents" | "entities" | "gate" | "recent";
const TABS: { key: TabKey; label: string }[] = [
	{ key: "intents", label: "意图表" },
	{ key: "entities", label: "业务对象与信息项" },
	{ key: "gate", label: "规则层与确认门" },
	{ key: "recent", label: "本轮信号留痕" },
];

/** 接续动作（语义层 resume，含义照信号提示词） */
const RESUME_ZH: Record<string, string> = {
	new: "开新事",
	follow_up: "延续已有事项",
	provide: "补信息",
	confirm: "同意",
	reject: "放弃",
	switch: "换事",
};
const RESUME_DESC: Record<string, string> = {
	new: "开一件新事",
	follow_up: "延续「已有事项」里的一件",
	provide: "回答「被问的问题」、补信息（在等补信息时说「好的 ／ 继续」也算这个）",
	confirm: "同意那一笔待确认的写操作",
	reject: "放弃那一笔待确认的操作，或说这件不要了",
	switch: "换成另一件事",
};
const TIER_TONE: Record<string, BadgeTone> = { 明确: "success", 可能: "info", 不确定: "muted" };
/** 「把握」（语义层 tier）的含义，表头悬浮提示用（照信号提示词） */
const TIER_HINT = "信号对「这件诉求是什么（意图）」有多大把握：明确 ／ 可能 ／ 不确定";
const CONF_HINT = "信号对「接续动作和接的哪件事项」判断的把握，0～1";
const SENT_TONE: Record<string, BadgeTone> = {
	negative: "danger",
	neutral: "muted",
	positive: "success",
};
const ALL = "__all__";
const NONE = "__none__";

/** 字典里的编号跳「标签字典」：标签编号打开那一条，维度编号切到那个维度 */
function DictLink({
	code,
	children,
	testId,
}: {
	code: string;
	children: React.ReactNode;
	testId?: string;
}) {
	return (
		<Link
			to="/plan-lab"
			search={{ tag: code }}
			hash="tags"
			title={`去标签字典看 ${code}`}
			onClick={(e) => e.stopPropagation()}
			className="text-text-secondary underline-offset-2 hover:text-brand-accent hover:underline"
			data-testid={testId}
		>
			{children}
		</Link>
	);
}

/** 意图在标签字典里的显示「一级 › 二级」与编号 */
type IntentInfo = { label: string; code: string | null };
function useIntentInfo(data: PlanSignalConfig): (k?: string | null) => IntentInfo {
	return useMemo(() => {
		const groups = Object.fromEntries(data.intent_table.groups.map((g) => [g.key, g.name]));
		const by = Object.fromEntries(data.intent_table.intents.map((x) => [x.key, x]));
		return (k?: string | null) => {
			if (!k) return { label: "（表外）", code: null };
			const x = by[k];
			if (!x) return { label: k, code: null };
			const g = x.parent ? groups[x.parent] : "";
			return { label: g ? `${g} › ${x.name}` : x.name, code: intentTagCode(x) };
		};
	}, [data]);
}
const VERDICT_TONE: Record<string, BadgeTone> = {
	confirm: "success",
	reject: "danger",
	correct: "warning",
	object_correct: "warning",
	unsure: "info",
	provide: "neutral",
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="space-y-1">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="whitespace-pre-wrap break-words text-sm text-text-primary">{children}</div>
		</div>
	);
}

function Trunc({ text, mono = false }: { text?: string | null; mono?: boolean }) {
	if (!text) return <span className="text-text-muted">—</span>;
	return (
		<span
			className={`block truncate text-text-secondary${mono ? " font-mono text-xs" : ""}`}
			title={text}
		>
			{text}
		</span>
	);
}

/** 规则编号：点了去「运行规则」页并打开那条 */
function RuleLink({ id }: { id: string }) {
	return (
		<Link
			to="/plan-lab"
			search={{ rule: id }}
			hash="guards"
			title={`去运行规则看 ${id}`}
			onClick={(e) => e.stopPropagation()}
			className="font-mono text-xs text-text-secondary underline-offset-2 hover:text-brand-accent hover:underline"
			data-testid="plan-signal-rule-link"
		>
			{id}
		</Link>
	);
}

function Section({
	title,
	note,
	children,
	testId,
}: {
	title: string;
	note?: React.ReactNode;
	children: React.ReactNode;
	testId?: string;
}) {
	return (
		<section className="space-y-2" data-testid={testId}>
			<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
				<h3 className="text-sm font-semibold text-text-primary">{title}</h3>
				{note ? <span className="text-xs text-text-muted">{note}</span> : null}
			</div>
			{children}
		</section>
	);
}

export function SignalTab() {
	const [tab, setTab] = useState<TabKey>("intents");
	const q = usePlanSignal();
	const data = q.data;

	return (
		<div className="space-y-3" data-testid="plan-block-signal">
			<Tabs items={TABS} active={tab} onChange={setTab} />
			{q.isLoading ? (
				<Card>
					<CardContent className="py-4">
						<LoadingInline />
					</CardContent>
				</Card>
			) : q.isError || !data ? (
				<Card>
					<CardContent>
						<FailState
							reason={planErrorMessage(q.error)}
							onRetry={() => q.refetch()}
							retrying={q.isFetching}
							testId="plan-signal-fail"
						/>
					</CardContent>
				</Card>
			) : (
				<>
					{tab === "intents" ? <IntentsPanel data={data} /> : null}
					{tab === "entities" ? <EntitiesPanel data={data} /> : null}
					{tab === "gate" ? <GatePanel data={data} /> : null}
					{tab === "recent" ? <RecentPanel data={data} /> : null}
				</>
			)}
		</div>
	);
}

/* ── ① 意图表（只读概览；维护口径在标签字典页） ───────── */

/** 意图在标签字典里的编号（`intent.<一级>.<键>`；没挂一级的 `intent.<键>`） */
function intentTagCode(x: { key: string; parent?: string }): string {
	return x.parent ? `intent.${x.parent}.${x.key}` : `intent.${x.key}`;
}

function IntentsPanel({ data }: { data: PlanSignalConfig }) {
	const navigate = useNavigate();
	const t = data.intent_table;
	const toDict = (x: PlanSignalIntent) =>
		navigate({ to: "/plan-lab", search: { tag: intentTagCode(x) }, hash: "tags" });
	const cols: PlanColumn<PlanSignalIntent>[] = [
		{
			key: "key",
			title: "键",
			className: "w-40",
			render: (x) => <span className="font-mono text-xs">{x.key}</span>,
		},
		{ key: "name", title: "名称", className: "w-28", render: (x) => x.name },
		{
			key: "parent",
			title: "一级",
			className: "w-24",
			render: (x) => x.parent_name || <span className="text-text-muted">—</span>,
		},
		{
			key: "desc",
			title: "页面短说明（不进提示词）",
			className: "max-w-[380px]",
			render: (x) => <Trunc text={x.desc} />,
		},
		{
			key: "creates",
			title: "建事项",
			className: "w-20",
			render: (x) =>
				x.creates_item ? (
					<span className="text-text-secondary">建</span>
				) : (
					<Badge tone="warning">不建</Badge>
				),
		},
		{
			key: "tools",
			title: "加分的工具",
			className: "max-w-[200px]",
			render: (x) => <CellTags mono max={2} items={x.tools} />,
		},
		{
			key: "guides",
			title: "加分的指南",
			className: "w-20",
			align: "right",
			render: (x) =>
				x.guides.length ? x.guides.length : <span className="text-text-muted">—</span>,
		},
	];
	const noItem = t.intents.filter((x) => !x.creates_item);
	return (
		<Card>
			<CardContent className="space-y-3 py-4" data-testid="plan-signal-intents">
				<p className="text-xs text-text-muted">
					语义层从这张表里给每件诉求选一个意图；提示词里渲染键、名称和说明里的定义、包含、不包含（例句只在后台，不进模型）。标「不建」的意图（
					{noItem.map((x) => x.name).join("、") || "无"}）只回话、不建事项（规则{" "}
					<RuleLink id="BRD-02" />
					）。一级、四段说明（定义 ／ 包含 ／ 不包含 ／ 例句）、被引用与体检在{" "}
					<Link
						to="/plan-lab"
						search={{}}
						hash="tags"
						className="text-text-secondary underline underline-offset-2 hover:text-brand-accent"
						data-testid="plan-signal-to-tags"
					>
						标签字典
					</Link>
					页看；点一行跳到那条。
				</p>
				<PlanTable
					columns={cols}
					rows={t.intents}
					rowKey={(x) => x.key}
					onRowClick={toDict}
					density="compact"
					rowTestId="plan-signal-intent-row"
				/>
				<div className="text-xs text-text-muted" data-testid="plan-signal-intents-count">
					意图表 <span className="font-mono">{t.version}</span>（{t.domain}）· 画像{" "}
					<span className="font-mono">{data.profile_version}</span> · 共 {t.intents.length}{" "}
					个，不建事项 {noItem.length} 个
				</div>
			</CardContent>
		</Card>
	);
}

/* ── ② 业务对象与信息项（定义读标签字典） ───────────── */

function DictNote({ dim, label }: { dim: string; label: string }) {
	return (
		<>
			定义在
			<DictLink code={dim} testId={`plan-signal-dict-${dim}`}>
				标签字典 › {label}
			</DictLink>
		</>
	);
}

function EntitiesPanel({ data }: { data: PlanSignalConfig }) {
	const navigate = useNavigate();
	const toDict = (code: string) =>
		navigate({ to: "/plan-lab", search: { tag: code }, hash: "tags" });
	type Obj = PlanSignalConfig["objects"][number];
	type Slot = PlanSignalConfig["slots"][number];
	const objCols: PlanColumn<Obj>[] = [
		{
			key: "key",
			title: "业务对象",
			className: "w-32",
			render: (e) => <span className="font-mono text-xs">{e.key}</span>,
		},
		{ key: "name", title: "显示名", className: "w-20", render: (e) => e.name },
		{
			key: "desc",
			title: "填写口径（给语义层）",
			className: "max-w-[420px]",
			render: (e) => <Trunc text={e.desc} />,
		},
		{
			key: "to",
			title: "核实后进事项",
			className: "max-w-[300px]",
			render: (e) => <Trunc text={e.to} mono />,
		},
		{
			key: "rule_id",
			title: "规则",
			className: "w-20",
			render: (e) => (e.rule_id ? <RuleLink id={e.rule_id} /> : "—"),
		},
	];
	const slotCols: PlanColumn<Slot>[] = [
		{
			key: "key",
			title: "信息项",
			className: "w-44",
			render: (s) => (
				<span className="flex items-center gap-1.5">
					<span className="font-mono text-xs">{s.key}</span>
					{s.from_entity ? (
						<span title={`原为实体 ${s.from_entity}，批 2 并进信息项`}>
							<Badge tone="muted">原为实体</Badge>
						</span>
					) : null}
				</span>
			),
		},
		{
			key: "desc",
			title: "说明（给语义层）",
			className: "max-w-[480px]",
			render: (s) => <Trunc text={s.desc} />,
		},
		{
			key: "to",
			title: "核实后进事项",
			className: "max-w-[260px]",
			render: (s) =>
				s.to ? (
					<Trunc text={s.to} mono />
				) : (
					<span className="text-text-muted" title="只在信号输出与召回实体路（工具 slots）里用">
						不进事项
					</span>
				),
		},
		{
			key: "rule_id",
			title: "规则",
			className: "w-20",
			render: (s) => (s.rule_id ? <RuleLink id={s.rule_id} /> : "—"),
		},
	];
	const merged = data.slots.filter((s) => s.from_entity).length;
	return (
		<Card>
			<CardContent className="space-y-5 py-4" data-testid="plan-signal-entities">
				<Section
					title="业务对象"
					note={
						<>
							<DictNote dim="object" label="业务对象" />
							{` · 生效画像 ${data.profile_version} · ${data.objects.length} 类；编号类只认核实过的；点一行看字典那一条`}
						</>
					}
					testId="plan-signal-objects"
				>
					<PlanTable
						columns={objCols}
						rows={data.objects}
						rowKey={(e) => e.key}
						onRowClick={(e) => toDict(e.code)}
						density="compact"
						rowTestId="plan-signal-object-row"
					/>
				</Section>
				<Section
					title="信息项（slots）"
					note={
						<>
							<DictNote dim="slot" label="信息项" />
							{` · 业务包 slot_keys · ${data.slots.length} 项，其中 ${merged} 项原为实体；只收这些键，没说的不填`}
						</>
					}
					testId="plan-signal-slots"
				>
					<PlanTable
						columns={slotCols}
						rows={data.slots}
						rowKey={(s) => s.key}
						onRowClick={(s) => toDict(s.code)}
						density="compact"
						rowTestId="plan-signal-slot-row"
					/>
				</Section>
				<Section
					title="退货原因枚举"
					note={
						<>
							不在枚举里的原因当没给，执行时缺参由 N2 决定问（规则 <RuleLink id="SIG-09" />
							）；括号里是所属的类，指南条件和知识例外按类引用
						</>
					}
				>
					<div className="flex flex-wrap gap-1">
						{(data.return_reason_values?.length
							? data.return_reason_values
							: data.return_reasons.map((r) => ({ value: r, name: r, classes: [] as string[] }))
						).map((r) => (
							<Badge key={r.value} tone="muted" className="font-mono">
								{r.value}
								{r.classes.length ? `（${r.classes.join("、")}）` : ""}
							</Badge>
						))}
					</div>
				</Section>
				<Section
					title="情绪与风险"
					note={
						<>
							<DictNote dim="emotion_risk" label="情绪与风险" />
							{
								" · 平台内置，信号同一次调用整轮给一份；不硬路由：命中指南的情绪与风险标签时加分，并给相关性判断与 N2 ／ N3 作参考（规则 "
							}
							<RuleLink id="SIG-16" />）
						</>
					}
					testId="plan-signal-emotion-risk"
				>
					<div className="space-y-2">
						{data.emotion_risk.map((g) => (
							<div key={g.key} className="flex flex-wrap items-center gap-1.5 text-sm">
								<span className="w-16 shrink-0 text-xs text-text-muted" title={g.desc}>
									{g.name}
								</span>
								{g.values.map((v) => (
									<DictLink key={v.key} code={v.code}>
										<span title={v.desc}>
											<Badge
												tone={g.key === "sentiment" ? (SENT_TONE[v.key] ?? "muted") : "warning"}
											>
												{v.name}
											</Badge>
										</span>
									</DictLink>
								))}
							</div>
						))}
					</div>
				</Section>
			</CardContent>
		</Card>
	);
}

/* ── ③ 规则层与确认门 ───────────────────────────────── */

const FLOW_STEPS: { title: string; text: string; rules: string[] }[] = [
	{
		title: "规则层",
		text: "按订单号写法从原话抽编号、逐个对客户名下订单，只留对得上的，作为「已确定的事实」",
		rules: ["SR-ORDER-NO", "SIG-01"],
	},
	{
		title: "输入视图",
		text: "非首轮：被问的问题与可选项、会话里全部事项，供语义层判接续",
		rules: ["SR-PENDING", "SR-ITEMS"],
	},
	{
		title: "语义层（一次模型调用）",
		text: "拆诉求清单：每件给目标、原话片段、业务对象、意图、意图把握、接续动作、接的事项、接续把握、信息项；整轮再给一份情绪与风险旗（只加分、作参考，不硬路由；「明确要人工」由意图派生，模型不另判）",
		rules: ["SIG-04", "SIG-05", "SIG-16"],
	},
	{
		title: "实体核实",
		text: "订单 ／ 行项只认名下或可选项里的，商品编号目录里有才收，商品名进关键词；信息项里退货原因映射枚举，地址 ／ 问题原话进事项；按商品词唯一定位订单",
		rules: ["SIG-06", "SIG-07", "SIG-08", "SR-PRODUCT-ORDER"],
	},
	{
		title: "确认门（事项板处理「同意」时调）",
		text: "客户对等确认的写操作说了话：按下面的顺序判能不能提交",
		rules: ["SIG-15", "BRD-03"],
	},
];

function GatePanel({ data }: { data: PlanSignalConfig }) {
	const navigate = useNavigate();
	const g = data.gate;
	const rules = useMemo(() => new Map(data.rules.map((r) => [r.id, r])), [data.rules]);
	const stepHow: Record<string, React.ReactNode> = {
		object_correct: "写操作事项已绑订单，本轮核实出的订单和绑定的不同（接续为放弃时不算）",
		reject: (
			<>
				接续为放弃 → 放弃；同意 ／ 补信息但原话带拒绝说法（{g.refuse_patterns.length} 条，见下）→
				拿不准、保留确认卡再问一次
			</>
		),
		correct: (
			<>
				信息项 <span className="font-mono text-xs">{g.correctable_slots.join("、")}</span>{" "}
				与卡上不同；或行项不同、要保留的商品落在提交范围里（排除）、点名的商品只是范围的一部分（范围收窄）
			</>
		),
		confirm: (
			<>
				接续为同意，档位须「{g.confirm_tier}」且把握 ≥ {g.confirm_min_confidence}
				；否则拿不准、不提交、再问一次
			</>
		),
		provide: "以上都不是 → 按补信息并入事项",
	};
	const stepResult: Record<string, string> = {
		object_correct: "作废确认卡、转回办理中，不改绑定",
		reject: "撤销事项",
		correct: "作废确认卡、转回办理中、新值并入",
		confirm: "交执行层真执行",
		provide: "并入补充信息",
	};
	type Step = PlanSignalConfig["gate"]["steps"][number] & { n: number };
	const steps: Step[] = g.steps.map((s, i) => ({ ...s, n: i + 1 }));
	const stepCols: PlanColumn<Step>[] = [
		{ key: "n", title: "顺序", className: "w-14", render: (s) => s.n },
		{
			key: "verdict",
			title: "判定",
			className: "w-28",
			render: (s) => (
				<Badge tone={VERDICT_TONE[s.verdict] ?? "muted"}>{zh(RESUME_ACTION_ZH, s.verdict)}</Badge>
			),
		},
		{
			key: "how",
			title: "怎么判",
			className: "min-w-[360px] max-w-[560px]",
			render: (s) => (
				<span className="block whitespace-normal py-1.5 text-text-secondary">
					{stepHow[s.verdict]}
				</span>
			),
		},
		{
			key: "result",
			title: "结果",
			className: "w-56",
			render: (s) => <Trunc text={stepResult[s.verdict]} />,
		},
		{
			key: "rules",
			title: "规则",
			className: "w-36",
			render: (s) =>
				s.rules.length ? (
					<span className="flex gap-2">
						{s.rules.map((r) => (
							<RuleLink key={r} id={r} />
						))}
					</span>
				) : (
					"—"
				),
		},
	];
	type Input = PlanSignalConfig["inputs"][number];
	const inputCols: PlanColumn<Input>[] = [
		{
			key: "key",
			title: "输入块",
			className: "w-44",
			render: (r) => (
				<span className="font-mono text-xs">{r.sub ? `${r.key}.${r.sub}` : r.key}</span>
			),
		},
		{
			key: "what",
			title: "是什么",
			className: "max-w-[260px]",
			render: (r) => <Trunc text={r.what} />,
		},
		{ key: "source", title: "从哪来", className: "w-32", render: (r) => <Trunc text={r.source} /> },
		{
			key: "when",
			title: "什么时候给",
			className: "max-w-[200px]",
			render: (r) => <Trunc text={r.when} />,
		},
		{
			key: "guard",
			title: "护栏",
			className: "max-w-[180px]",
			render: (r) => <Trunc text={r.guard} />,
		},
	];
	type SigRule = PlanSignalConfig["rules"][number];
	const ruleCols: PlanColumn<SigRule>[] = [
		{ key: "id", title: "编号", className: "w-40", render: (r) => <RuleLink id={r.id} /> },
		{ key: "stage", title: "环节", className: "w-20", render: (r) => r.stage },
		{
			key: "name",
			title: "名称",
			className: "max-w-[180px]",
			render: (r) => (
				<span className="block truncate" title={r.name}>
					{r.name}
				</span>
			),
		},
		{
			key: "does",
			title: "做什么",
			className: "max-w-[520px]",
			render: (r) => <Trunc text={r.does} />,
		},
		{
			key: "kind",
			title: "类型",
			className: "w-24",
			render: (r) => <Badge tone="muted">{r.kind}</Badge>,
		},
	];
	return (
		<Card>
			<CardContent className="space-y-5 py-4" data-testid="plan-signal-gate">
				<Section title="一轮里信号怎么走" note="信号只理解：不选工具、不判能不能办、不改事项">
					<ol className="space-y-1.5 text-sm">
						{FLOW_STEPS.map((s, i) => (
							<li key={s.title} className="flex flex-wrap items-baseline gap-x-2">
								<span className="text-text-muted">{i + 1}.</span>
								<span className="font-medium text-text-primary">{s.title}</span>
								<span className="text-text-secondary">{s.text}</span>
								<span className="flex gap-2">
									{s.rules
										.filter((r) => rules.has(r))
										.map((r) => (
											<RuleLink key={r} id={r} />
										))}
								</span>
							</li>
						))}
					</ol>
				</Section>

				<Section
					title="规则层"
					note={
						<>
							代码做的确定性加工，不调模型；订单号怎么比对等平台口径见{" "}
							<PageJump to="business-params" />
						</>
					}
				>
					<div className="grid gap-3 sm:grid-cols-2">
						<Field label="订单号写法（业务包 platform.order_no_pattern）">
							<span className="font-mono text-xs">{data.rule_layer.order_no_pattern}</span>{" "}
							<RuleLink id="SR-ORDER-NO" />
						</Field>
						<Field label="商品编号写法（写死）">
							<span className="font-mono text-xs">{data.rule_layer.product_id_pattern}</span>{" "}
							<RuleLink id="SIG-08" />
						</Field>
					</div>
				</Section>

				<Section
					title="语义层：把握与接续"
					note="表外的值归一：意图把握按不确定、接续按没指明；情绪与风险表外的值丢弃并记一笔（SIG-16）"
				>
					<div className="flex flex-wrap items-center gap-1.5 text-sm">
						<span className="text-xs text-text-muted" title={TIER_HINT}>
							意图把握
						</span>
						{data.tiers.map((t) => (
							<Badge key={t} tone={TIER_TONE[t] ?? "muted"}>
								{t}
							</Badge>
						))}
					</div>
					<ul
						className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2"
						data-testid="plan-signal-resumes"
					>
						{data.resumes.map((r) => (
							<li key={r} className="flex items-baseline gap-2">
								<span className="w-20 shrink-0 font-mono text-xs text-text-secondary">{r}</span>
								<span className="shrink-0 text-text-primary">{RESUME_ZH[r] ?? r}</span>
								<span className="truncate text-xs text-text-muted" title={RESUME_DESC[r]}>
									{RESUME_DESC[r] ?? ""}
								</span>
							</li>
						))}
					</ul>
				</Section>

				<Section
					title="语义层输入块"
					note={
						<>
							<PageJump to="info-flow" /> 注册表 node=signal；提示词在生效快照里，去{" "}
							<Link
								to="/plan-lab"
								hash="prompts"
								className="underline-offset-2 hover:text-brand-accent hover:underline"
							>
								模型提示
							</Link>{" "}
							看
						</>
					}
				>
					<PlanTable
						columns={inputCols}
						rows={data.inputs}
						rowKey={(r) => `${r.key}.${r.sub}`}
						density="compact"
					/>
				</Section>

				<Section
					title="确认门判定顺序"
					note={
						<>
							从上往下，命中即停（规则 <RuleLink id={g.order_rule} />）
						</>
					}
					testId="plan-signal-gate-steps"
				>
					<PlanTable
						columns={stepCols}
						rows={steps}
						rowKey={(s) => s.verdict}
						density="compact"
						rowTestId="plan-signal-gate-row"
					/>
				</Section>

				<Section
					title="拒绝说法"
					note={
						<>
							业务包 refuse_patterns · {g.refuse_patterns.length} 条正则（规则{" "}
							<RuleLink id="SR-REFUSE" />）
						</>
					}
				>
					<div className="flex flex-wrap gap-1" data-testid="plan-signal-refuse">
						{g.refuse_patterns.map((p) => (
							<Badge key={p} tone="muted" className="font-mono">
								{p}
							</Badge>
						))}
					</div>
				</Section>

				<Section
					title="信号相关规则"
					note={`运行规则注册表：环节=信号，外加事项板里的确认门与建不建事项 · 共 ${data.rules.length} 条；点一行去运行规则页`}
				>
					<PlanTable
						columns={ruleCols}
						rows={data.rules}
						rowKey={(r) => r.id}
						onRowClick={(r) =>
							navigate({ to: "/plan-lab", search: { rule: r.id }, hash: "guards" })
						}
						density="compact"
						rowTestId="plan-signal-rule-row"
					/>
				</Section>
			</CardContent>
		</Card>
	);
}

/* ── ④ 本轮信号留痕 ─────────────────────────────────── */

function fmtTime(at: string): string {
	const d = new Date(at);
	if (Number.isNaN(d.getTime())) return at;
	const p = (n: number) => String(n).padStart(2, "0");
	return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

const uniq = (xs: (string | null | undefined)[]) => [
	...new Set(xs.filter((x): x is string => !!x)),
];

function RecentPanel({ data }: { data: PlanSignalConfig }) {
	const [limit, setLimit] = useState("20");
	const [sent, setSent] = useState(ALL);
	const [risk, setRisk] = useState(ALL);
	const q = usePlanSignalRecent(Number(limit));
	const [open, setOpen] = useState<PlanSignalTurn | null>(null);
	const intentInfo = useIntentInfo(data);
	const er = useMemo(() => {
		const g = Object.fromEntries(data.emotion_risk.map((x) => [x.key, x]));
		return {
			sentiment: g.sentiment?.values ?? [],
			risk: g.risk_flags?.values ?? [],
			name: (group: string, k: string) => g[group]?.values.find((v) => v.key === k)?.name ?? k,
			code: (group: string, k: string) =>
				g[group]?.values.find((v) => v.key === k)?.code ?? `emotion_risk.${group}.${k}`,
		};
	}, [data]);

	const rows = (q.data?.rows ?? []).filter((r) => {
		const s = r.sentiment ?? null;
		const flags = r.risk_flags ?? [];
		if (sent !== ALL && (sent === NONE ? s !== null : s !== sent)) return false;
		if (risk === ALL) return true;
		if (risk === NONE) return !flags.length;
		if (risk === "__any__") return flags.length > 0;
		return flags.includes(risk);
	});

	const cols: PlanColumn<PlanSignalTurn>[] = [
		{
			key: "at",
			title: "时间",
			className: "w-32",
			render: (r) => <span className="font-mono text-xs">{fmtTime(r.at)}</span>,
		},
		{
			key: "source",
			title: "来源",
			className: "w-20",
			render: (r) => (
				<Badge tone={SOURCE_TONE[r.source ?? ""] ?? "muted"}>{zh(SOURCE_ZH, r.source)}</Badge>
			),
		},
		{
			key: "message",
			title: "客户原话",
			className: "max-w-[200px]",
			render: (r) => (
				<span className="block truncate text-text-primary" title={r.message}>
					{r.message || "—"}
				</span>
			),
		},
		{
			key: "intent",
			title: "诉求意图（一级 › 二级）",
			className: "max-w-[230px]",
			render: (r) =>
				r.demands.length ? (
					<IntentLinks infos={r.demands.map((d) => intentInfo(d.demand_type))} max={2} />
				) : (
					<span className="text-text-muted">无诉求</span>
				),
		},
		{
			key: "tier",
			title: (
				<span
					title={TIER_HINT}
					className="cursor-help underline decoration-dotted underline-offset-2"
				>
					把握
				</span>
			),
			className: "w-20",
			render: (r) => <CellTags max={1} items={uniq(r.demands.map((d) => d.tier))} />,
		},
		{
			key: "resume",
			title: "接续",
			className: "max-w-[140px]",
			render: (r) => (
				<CellTags
					max={2}
					items={r.demands.map((d) => (d.resume ? (RESUME_ZH[d.resume] ?? d.resume) : "没指明"))}
				/>
			),
		},
		{
			key: "sentiment",
			title: "情绪",
			className: "w-16",
			render: (r) =>
				r.sentiment ? (
					<span data-testid="plan-signal-sentiment">
						<Badge tone={SENT_TONE[r.sentiment] ?? "muted"}>
							{er.name("sentiment", r.sentiment)}
						</Badge>
					</span>
				) : (
					<span className="text-text-muted">—</span>
				),
		},
		{
			key: "risk",
			title: "风险",
			className: "max-w-[160px]",
			render: (r) =>
				r.risk_flags?.length ? (
					<span data-testid="plan-signal-risk">
						<CellTags
							tone="warning"
							max={1}
							items={r.risk_flags.map((k) => er.name("risk_flags", k))}
						/>
					</span>
				) : (
					<span className="text-text-muted">—</span>
				),
		},
		{
			key: "target",
			title: "目标事项",
			className: "w-24",
			render: (r) => <CellTags mono max={1} items={uniq(r.demands.map((d) => d.target_item))} />,
		},
		{
			key: "verdicts",
			title: "确认门",
			className: "w-24",
			render: (r) =>
				r.verdicts.length ? (
					<span className="inline-flex gap-1">
						{r.verdicts.slice(0, 2).map((v, i) => (
							<Badge key={`${i}:${v.verdict}`} tone={VERDICT_TONE[v.verdict] ?? "muted"}>
								{zh(RESUME_ACTION_ZH, v.verdict)}
							</Badge>
						))}
					</span>
				) : (
					<span className="text-text-muted">—</span>
				),
		},
		{
			key: "hits",
			title: "规则命中",
			className: "max-w-[160px]",
			render: (r) =>
				r.degraded ? (
					<span title={r.degraded}>
						<Badge tone="danger">降级</Badge>
					</span>
				) : (
					<CellTags mono max={2} items={r.rule_hits} />
				),
		},
	];

	return (
		<Card>
			<CardContent className="space-y-3 py-4" data-testid="plan-signal-recent">
				<div className="flex flex-wrap items-center gap-2">
					<LabeledSelect
						label="条数"
						value={limit}
						onChange={setLimit}
						options={["20", "50", "100"].map((v) => ({ value: v, label: `最近 ${v} 轮` }))}
					/>
					<span data-testid="plan-signal-filter-sentiment" className="contents">
						<LabeledSelect
							label="情绪"
							value={sent}
							onChange={setSent}
							options={[
								{ value: ALL, label: "全部" },
								...er.sentiment.map((v) => ({ value: v.key, label: v.name })),
								{ value: NONE, label: "没有（老留痕）" },
							]}
						/>
					</span>
					<span data-testid="plan-signal-filter-risk" className="contents">
						<LabeledSelect
							label="风险"
							value={risk}
							onChange={setRisk}
							options={[
								{ value: ALL, label: "全部" },
								{ value: "__any__", label: "有风险旗" },
								...er.risk.map((v) => ({ value: v.key, label: v.name })),
								{ value: NONE, label: "无风险旗" },
							]}
						/>
					</span>
					<span className="text-xs text-text-muted">
						情绪与风险不硬路由：命中指南的情绪与风险标签时加分，并给相关性判断与 N2 ／ N3
						作参考；取值见 <DictLink code="emotion_risk">标签字典 › 情绪与风险</DictLink>
						；盲测题的会话不展示
					</span>
				</div>
				{q.isLoading ? (
					<LoadingInline />
				) : q.isError || !q.data ? (
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-signal-recent-fail"
					/>
				) : q.data.rows.length ? (
					<>
						{rows.length ? (
							<PlanTable
								columns={cols}
								rows={rows}
								rowKey={(r) => r.trace_id}
								onRowClick={setOpen}
								density="compact"
								rowTestId="plan-signal-turn-row"
							/>
						) : (
							<EmptyState title="没有符合筛选的轮次" className="py-8" />
						)}
						<div className="text-xs text-text-muted" data-testid="plan-signal-recent-count">
							近 {q.data.days} 天 · 共 {q.data.rows.length} 轮
							{rows.length !== q.data.rows.length ? `（筛出 ${rows.length} 轮）` : ""}
							{q.data.skipped_sealed ? ` · 跳过盲测 ${q.data.skipped_sealed} 轮` : ""}
							{q.data.truncated ? " · 扫描到上限，更早的没读" : ""}
						</div>
					</>
				) : (
					<EmptyState
						title="近 3 天没有信号留痕"
						desc="在「人工提问」问一句，或跑一批模拟评测后再来看。"
					/>
				)}
			</CardContent>
			<Drawer
				open={!!open}
				onClose={() => setOpen(null)}
				width="min(860px,95vw)"
				testId="plan-signal-turn-drawer"
				title={<span className="truncate">「{open?.message}」</span>}
				description={
					open
						? `${fmtTime(open.at)} · ${zh(SOURCE_ZH, open.source)}${open.turn_no ? ` · 第 ${open.turn_no} 轮` : ""} · ${open.trace_id}`
						: undefined
				}
				badges={open?.degraded ? <Badge tone="danger">降级 {open.degraded}</Badge> : null}
			>
				{open ? (
					<TurnDetail t={open} intentInfo={intentInfo} erName={er.name} erCode={er.code} />
				) : null}
			</Drawer>
		</Card>
	);
}

/** 诉求意图「一级 › 二级」，点了跳标签字典那一条；多于 max 个显示 +N */
function IntentLinks({ infos, max }: { infos: IntentInfo[]; max: number }) {
	const shown = infos.slice(0, max);
	return (
		<span className="flex min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap">
			{shown.map((x, i) =>
				x.code ? (
					<DictLink key={`${i}:${x.code}`} code={x.code} testId="plan-signal-intent-link">
						<span className="text-xs">{x.label}</span>
					</DictLink>
				) : (
					<span key={`${i}:${x.label}`} className="text-xs text-text-muted">
						{x.label}
					</span>
				),
			)}
			{infos.length > max ? (
				<span className="text-xs text-text-muted" title={infos.map((x) => x.label).join("、")}>
					+{infos.length - max}
				</span>
			) : null}
		</span>
	);
}

function TurnDetail({
	t,
	intentInfo,
	erName,
	erCode,
}: {
	t: PlanSignalTurn;
	intentInfo: (k?: string | null) => IntentInfo;
	erName: (group: string, k: string) => string;
	erCode: (group: string, k: string) => string;
}) {
	type D = PlanSignalTurn["demands"][number];
	const dCols: PlanColumn<D>[] = [
		{
			key: "id",
			title: "编号",
			className: "w-14",
			render: (d) => <span className="font-mono">{d.demand_id}</span>,
		},
		{
			key: "goal",
			title: "目标",
			className: "max-w-[200px]",
			render: (d) => <Trunc text={d.goal} />,
		},
		{
			key: "intent",
			title: "意图（一级 › 二级）",
			className: "max-w-[160px]",
			render: (d) => <IntentLinks infos={[intentInfo(d.demand_type)]} max={1} />,
		},
		{
			key: "tier",
			title: <span title={TIER_HINT}>意图把握</span>,
			className: "w-20",
			render: (d) => <Badge tone={TIER_TONE[d.tier ?? ""] ?? "muted"}>{d.tier ?? "—"}</Badge>,
		},
		{
			key: "resume",
			title: "接续",
			className: "w-24",
			render: (d) => (d.resume ? (RESUME_ZH[d.resume] ?? d.resume) : "没指明"),
		},
		{
			key: "target",
			title: "目标事项",
			className: "w-24",
			render: (d) => <Trunc text={d.target_item} mono />,
		},
		{
			key: "conf",
			title: <span title={CONF_HINT}>接续把握</span>,
			className: "w-16",
			align: "right",
			render: (d) => (typeof d.confidence === "number" ? d.confidence.toFixed(2) : "—"),
		},
		{
			key: "card",
			title: (
				<span title="确认卡挂着时信号另给的两项：改了卡上哪几项（有就判纠正、作废卡，SIG-13）；是不是在问这笔操作的后果（是就暂缓提交、先答再确认，SR-CONFIRM-Q）">
					对确认卡
				</span>
			),
			className: "max-w-[180px]",
			render: (d) =>
				d.rewrites?.length ? (
					<Trunc text={`改了：${d.rewrites.join("；")}`} />
				) : d.asks_pending ? (
					<Badge tone="warning">问这笔的后果</Badge>
				) : (
					"—"
				),
		},
		{
			key: "ents",
			title: "核实后对象与信息项",
			className: "max-w-[200px]",
			render: (d) => (
				<Trunc mono text={d.ents && Object.keys(d.ents).length ? JSON.stringify(d.ents) : ""} />
			),
		},
	];
	const flags = t.risk_flags ?? [];
	return (
		<>
			<DrawerSection title="这句话">
				<Field label="客户原话">{t.message || "—"}</Field>
				{t.reply ? <Field label="本轮回复">{t.reply}</Field> : null}
				{t.pending ? (
					<Field label="被问的问题（SR-PENDING）">
						<span className="font-mono text-xs">{JSON.stringify(t.pending)}</span>
					</Field>
				) : null}
			</DrawerSection>
			<DrawerSection title="情绪与风险（只加分、作参考）" testId="plan-signal-turn-emotion">
				<div className="flex flex-wrap items-center gap-2 text-sm">
					<span className="text-xs text-text-muted">情绪</span>
					{t.sentiment ? (
						<DictLink code={erCode("sentiment", t.sentiment)}>
							<Badge tone={SENT_TONE[t.sentiment] ?? "muted"}>
								{erName("sentiment", t.sentiment)}
							</Badge>
						</DictLink>
					) : (
						<span className="text-text-muted">—</span>
					)}
					<span className="ml-3 text-xs text-text-muted">风险旗</span>
					{flags.length ? (
						flags.map((k) => (
							<DictLink key={k} code={erCode("risk_flags", k)}>
								<Badge tone="warning">{erName("risk_flags", k)}</Badge>
							</DictLink>
						))
					) : (
						<span className="text-text-muted">无</span>
					)}
				</div>
				{t.emotion_risk_dropped?.length ? (
					<div className="text-xs text-text-muted">
						丢弃的表外值（SIG-16）：
						<span className="font-mono">{JSON.stringify(t.emotion_risk_dropped)}</span>
					</div>
				) : null}
			</DrawerSection>
			<DrawerSection title={`拆出的诉求（${t.demands.length}）`} testId="plan-signal-turn-demands">
				{t.demands.length ? (
					<PlanTable
						columns={dCols}
						rows={t.demands}
						rowKey={(d) => d.demand_id}
						density="compact"
					/>
				) : (
					<span className="text-sm text-text-muted">
						没有诉求{t.degraded ? `（降级：${t.degraded}，本轮不建、不并）` : ""}
					</span>
				)}
			</DrawerSection>
			<DrawerSection title="确认门判定" testId="plan-signal-turn-verdicts">
				{t.verdicts.length ? (
					<ul className="space-y-1.5 text-sm">
						{t.verdicts.map((v, i) => {
							const { demand_id, item_id, verdict, ...rest } = v;
							return (
								<li key={`${i}:${verdict}`} className="flex flex-wrap items-center gap-2">
									<span className="font-mono text-xs text-text-muted">
										{demand_id} → {item_id}
									</span>
									<Badge tone={VERDICT_TONE[verdict] ?? "muted"}>
										{zh(RESUME_ACTION_ZH, verdict)}
									</Badge>
									{Object.keys(rest).length ? (
										<span className="font-mono text-xs text-text-secondary">
											{JSON.stringify(rest)}
										</span>
									) : null}
								</li>
							);
						})}
					</ul>
				) : (
					<span className="text-sm text-text-muted">本轮没有等确认的事项，不过确认门。</span>
				)}
				{t.confirm_voided ? <Badge tone="warning">确认卡已作废</Badge> : null}
			</DrawerSection>
			<DrawerSection title="规则层">
				<Field label="命中的规则">
					{t.rule_hits.length ? (
						<span className="flex flex-wrap gap-2">
							{t.rule_hits.map((r) => (
								<RuleLink key={r} id={r} />
							))}
						</span>
					) : (
						"无"
					)}
				</Field>
			</DrawerSection>
			<DrawerSection title="完整 JSON 摘要">
				<JsonBlock value={t} maxHeight={360} />
			</DrawerSection>
		</>
	);
}
